import { type LabModelId, labModel } from '@shkola/api';
import { Suspense, lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../components/ui';
import { PageHead } from '../design/forms';
import { FitChips } from './LabPage';
import { BackToLab } from './SimPage';

// three.js is heavy: it comes only with a model
const ModelView = lazy(() => import('./ModelView').then((m) => ({ default: m.ModelView })));

export function ModelPage({ id }: { id: string }) {
  const { t } = useTranslation();
  const model = labModel(id);
  if (!model) return <BackToLab tab="models">{t('lab.back')}</BackToLab>;
  // «Форми 2»: back as a small pill, the name on the paper (no emoji), the model in its blocks
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <BackToLab tab="models">{t('lab.back')}</BackToLab>
        <PageHead
          title={model.title}
          sub={
            <span className="flex flex-col gap-2.5">
              <span className="text-[15px]">{model.about}</span>
              <FitChips fits={model.fits} />
            </span>
          }
        />
      </div>
      <Suspense fallback={<Spinner />}>
        <ModelView id={model.id} />
      </Suspense>
    </div>
  );
}

/**
 * The same model without the app around it and without signing in: the phone app shows it in a
 * WebView, and opens it in Safari for AR (`?ar=1`).
 */
export function PublicModelPage({ id }: { id: string }) {
  const model = labModel(id);
  useEffect(() => {
    if (model) document.title = model.title;
  }, [model]);
  if (!model) return null;
  return (
    <Suspense fallback={<Spinner />}>
      <ModelView id={model.id as LabModelId} embed />
    </Suspense>
  );
}
